import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import SEOHead from '@/components/base/SEOHead';
import SchemaOrg from '@/components/base/SchemaOrg';
import ContactSection from './components/ContactSection';

const aboutSchema = {
  '@context': 'https://schema.org',
  '@type': 'AboutPage',
  name: '关于狠狠爱',
  description: '「狠狠爱」诞生于一个简单的信念：每个人都值得被深爱，每个人都有能力去深爱。了解狠狠爱的品牌故事、口碑评价和联系方式。',
  url: 'https://henhenai.com/about',
  inLanguage: 'zh-CN',
  isPartOf: { '@type': 'WebSite', name: '狠狠爱', url: 'https://henhenai.com/' },
};

const breadcrumbSchema = {
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [
    { '@type': 'ListItem', position: 1, name: '首页', item: 'https://henhenai.com/' },
    { '@type': 'ListItem', position: 2, name: '关于我们', item: 'https://henhenai.com/about' },
  ],
};

const faqSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: '「狠狠爱」是什么？',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '「狠狠爱」是一个专注于深度情感探索的中文内容平台。我们提供关于爱自己、爱他人、情感表达、情感成长等方面的优质文章和故事，帮助每个人学会全力以赴地去爱。',
      },
    },
    {
      '@type': 'Question',
      name: '「狠狠爱」的内容更新频率是怎样的？',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '我们每周发布3-5篇优质原创文章，涵盖自我关爱、伴侣关系、情感成长等多个领域。同时，我们也会定期推出深度专题系列，帮助读者系统地探索情感世界。',
      },
    },
    {
      '@type': 'Question',
      name: '我可以投稿给「狠狠爱」吗？',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '当然可以！我们非常欢迎来自读者的真实故事和情感感悟。请通过我们的联系邮箱 hello@henhenai.com 发送您的稿件，我们的编辑团队会在3个工作日内回复。',
      },
    },
    {
      '@type': 'Question',
      name: '如何第一时间获取「狠狠爱」的新内容？',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '您可以关注我们的社交媒体账号（微信、微博、小红书），或者定期访问我们的网站首页。我们也在计划推出邮件订阅功能，敬请期待！',
      },
    },
  ],
};

export default function AboutPage() {
  return (
    <>
      <SEOHead
        title="关于我们"
        description="「狠狠爱」诞生于一个简单的信念：每个人都值得被深爱，每个人都有能力去深爱。了解狠狠爱的品牌故事、口碑评价和联系方式。"
        keywords="狠狠爱,关于我们,品牌故事,联系方式,情感平台"
        canonicalUrl="https://henhenai.com/about"
        ogTitle="关于狠狠爱 - 品牌故事与联系方式"
      />
      <SchemaOrg data={[aboutSchema, breadcrumbSchema, faqSchema]} />
      <Navbar />
      <main className="min-h-screen pt-20">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <Breadcrumb items={[{ label: '首页', href: '/' }, { label: '关于我们' }]} />

          <section className="py-10 md:py-14">
            <div className="text-center mb-14">
              <h1 className="font-heading text-3xl md:text-5xl text-foreground-950 mb-4">关于狠狠爱</h1>
              <p className="text-base text-foreground-600 max-w-2xl mx-auto leading-relaxed">
                不顾一切、全力以赴的爱。让每一份情感都燃烧得更有意义。
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 mb-16">
              <div className="relative rounded-lg overflow-hidden aspect-[4/3]">
                <img
                  src="https://readdy.ai/api/search-image?query=A%20beautiful%20and%20warm%20cozy%20creative%20studio%20workspace%20with%20wooden%20desk%20laptop%20notebooks%20dried%20flowers%20in%20a%20ceramic%20vase%20and%20soft%20warm%20sunlight%20streaming%20through%20large%20windows%20cream%20and%20beige%20tones%20inspiring%20and%20peaceful%20atmosphere%20editorial%20photography&width=800&height=600&seq=about-brand-03&orientation=landscape"
                  alt="狠狠爱品牌工作室"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="flex flex-col justify-center">
                <h2 className="font-heading text-xl md:text-2xl text-foreground-950 mb-5">品牌故事</h2>
                <div className="space-y-4 text-sm text-foreground-700 leading-relaxed">
                  <p>
                    <strong>「狠狠爱」</strong>诞生于2024年初春。创始人小雨在一次深夜的独处中，突然意识到——太多的人在爱这件事上，总是小心翼翼、顾虑重重。我们害怕受伤，害怕被拒绝，害怕付出没有回报，于是选择了"适度地爱"。
                  </p>
                  <p>
                    但爱不应该是这样的。真正的爱，是<strong>不顾一切</strong>的勇气，是<strong>毫无保留</strong>的真诚，是<strong>全力以赴</strong>的奔赴。无论是爱自己、爱伴侣、爱家人还是爱朋友，都值得"狠狠"地投入。
                  </p>
                  <p>
                    于是，「狠狠爱」诞生了。我们汇聚了一批热爱文字、热爱生活的内容创作者，他们用自己的经历和感悟，写下一篇篇真挚的文章。希望每一个读到这些文字的人，都能找到属于自己的"狠狠爱"的力量。
                  </p>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-16">
              {[
                { number: '50+', label: '原创文章' },
                { number: '10万+', label: '月阅读量' },
                { number: '5', label: '内容分类' },
                { number: '98%', label: '读者好评' },
              ].map((stat) => (
                <div
                  key={stat.label}
                  className="bg-background-100 rounded-lg border border-background-200/70 p-6 text-center"
                >
                  <p className="font-heading text-3xl text-primary-500 mb-2">{stat.number}</p>
                  <p className="text-sm text-foreground-600">{stat.label}</p>
                </div>
              ))}
            </div>

            <div className="mb-16">
              <h2 className="font-heading text-xl md:text-2xl text-foreground-950 mb-8 text-center">读者口碑</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                {[
                  {
                    quote: '在这里找到了"狠狠爱自己"的勇气。每一篇文章都像是一个温暖的朋友在对我说话。推荐给每一个需要被治愈的人。',
                    name: '小雅',
                    role: '忠实读者 · 2年',
                    avatar: 'ri-user-heart-line',
                  },
                  {
                    quote: '我和伴侣一起读「狠狠爱一个人」系列文章，我们的关系变得比以前更加亲密和坦诚。感谢这个平台！',
                    name: '大鹏',
                    role: '忠实读者 · 1年',
                    avatar: 'ri-user-smile-line',
                  },
                  {
                    quote: '作为一个刚刚经历分手的人，"情感成长之路"系列给了我莫大的力量。现在我已经能够微笑面对过去了。',
                    name: '阿琳',
                    role: '忠实读者 · 半年',
                    avatar: 'ri-user-star-line',
                  },
                ].map((testimonial, index) => (
                  <div
                    key={index}
                    className="bg-background-100 rounded-lg border border-background-200/70 p-6 flex flex-col"
                  >
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-100 text-accent-600">
                        <i className={`${testimonial.avatar} text-lg`}></i>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-foreground-900">{testimonial.name}</p>
                        <p className="text-xs text-foreground-500">{testimonial.role}</p>
                      </div>
                    </div>
                    <p className="text-sm text-foreground-700 leading-relaxed flex-1">
                      &ldquo;{testimonial.quote}&rdquo;
                    </p>
                  </div>
                ))}
              </div>
            </div>

            <ContactSection />

            <div className="mb-16">
              <h2 className="font-heading text-xl md:text-2xl text-foreground-950 mb-8 text-center">常见问题</h2>
              <div className="max-w-3xl mx-auto space-y-4">
                {faqSchema.mainEntity.map((item, index) => (
                  <details
                    key={index}
                    className="bg-background-100 rounded-lg border border-background-200/70 p-5 group cursor-pointer"
                  >
                    <summary className="font-heading text-base text-foreground-900 list-none flex items-center justify-between">
                      {item.name}
                      <span className="w-5 h-5 flex items-center justify-center text-foreground-400 group-open:rotate-180 transition-transform duration-200">
                        <i className="ri-arrow-down-s-line"></i>
                      </span>
                    </summary>
                    <p className="mt-3 text-sm text-foreground-700 leading-relaxed">
                      {item.acceptedAnswer.text}
                    </p>
                  </details>
                ))}
              </div>
            </div>
          </section>
        </div>
      </main>
      <Footer />
    </>
  );
}